import { Box, Container, Typography, Card, CardContent, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion } from 'framer-motion';
import WebIcon from '@mui/icons-material/Web';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const services = [
  {
    icon: <WebIcon sx={{ fontSize: 36 }} />,
    title: 'Landing Pages',
    description: 'High-converting, lightning-fast landing pages built to capture leads and impress visitors from the first second.',
    price: '£499',
    color: '#7C3AED',
    features: ['Mobile-first design', 'SEO optimised', 'CTA strategy', 'Analytics setup'],
  },
  {
    icon: <ShoppingCartIcon sx={{ fontSize: 36 }} />,
    title: 'E-Commerce Sites',
    description: 'Full-featured online stores with product management, secure payments, and seamless checkout experiences.',
    price: '£1,499',
    color: '#F59E0B',
    features: ['Stripe / PayPal', 'Product CMS', 'Order management', 'Inventory tracking'],
  },
  {
    icon: <EventAvailableIcon sx={{ fontSize: 36 }} />,
    title: 'Booking Systems',
    description: 'Smart appointment booking apps for salons, consultants, gyms, and service businesses of all sizes.',
    price: '£999',
    color: '#10B981',
    features: ['Calendar integration', 'Email reminders', 'Online payments', 'Client portal'],
  },
  {
    icon: <DashboardIcon sx={{ fontSize: 36 }} />,
    title: 'Business Dashboards',
    description: 'Real-time analytics dashboards that transform your data into clear, actionable business insights.',
    price: '£1,999',
    color: '#06B6D4',
    features: ['Live data charts', 'KPI tracking', 'Custom reports', 'Role-based access'],
  },
  {
    icon: <ContactMailIcon sx={{ fontSize: 36 }} />,
    title: 'Contact Form Apps',
    description: 'Professional enquiry forms with smart routing, CRM integration, and automated response workflows.',
    price: '£299',
    color: '#E879F9',
    features: ['Spam protection', 'Email automation', 'CRM sync', 'File uploads'],
  },
  {
    icon: <TrendingUpIcon sx={{ fontSize: 36 }} />,
    title: 'SEO-Optimized Sites',
    description: 'Websites engineered from the ground up for search visibility, speed scores, and organic growth.',
    price: '£799',
    color: '#F97316',
    features: ['Core Web Vitals', 'Schema markup', 'Meta strategy', 'Sitemap & robots.txt'],
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

export default function Services() {
  return (
    <Box
      id="services"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(180deg, #111120 0%, #0F0F1A 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background accents */}
      <Box sx={{ position: 'absolute', width: 400, height: 400, borderRadius: '50%', background: '#F59E0B', filter: 'blur(140px)', opacity: 0.05, top: 0, right: 0, pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', width: 350, height: 350, borderRadius: '50%', background: '#7C3AED', filter: 'blur(140px)', opacity: 0.06, bottom: 0, left: 0, pointerEvents: 'none' }} />

      {/* Dot grid */}
      <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)', backgroundSize: '40px 40px', pointerEvents: 'none' }} />

      <Container maxWidth="lg">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
        >
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Box
              sx={{
                display: 'inline-block',
                px: 2, py: 0.5,
                borderRadius: 50,
                background: 'rgba(245,158,11,0.12)',
                border: '1px solid rgba(245,158,11,0.3)',
                mb: 2,
              }}
            >
              <Typography variant="overline" sx={{ color: '#FCD34D', fontWeight: 700, letterSpacing: 2 }}>
                Services
              </Typography>
            </Box>
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 800, mb: 2 }}>
              What I{' '}
              <Box component="span" className="gradient-text">Build</Box>
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.55)', maxWidth: 500, mx: 'auto', fontSize: '1.05rem' }}>
              End-to-end web solutions tailored to small businesses ready to grow their online presence.
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={3}>
          {services.map((service, i) => (
            <Grid key={service.title} size={{ xs: 12, sm: 6, md: 4 }}>
              <motion.div
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                whileHover={{ y: -8 }}
                style={{ height: '100%' }}
              >
                <Card
                  sx={{
                    height: '100%',
                    background: 'rgba(255,255,255,0.04)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '18px',
                    overflow: 'hidden',
                    position: 'relative',
                    cursor: 'default',
                    transition: 'all 0.35s ease',
                    '&:hover': {
                      border: `1px solid ${service.color}40`,
                      boxShadow: `0 20px 50px ${service.color}20`,
                      background: 'rgba(255,255,255,0.06)',
                    },
                  }}
                >
                  {/* Top color accent bar */}
                  <Box
                    sx={{
                      height: 4,
                      background: `linear-gradient(90deg, ${service.color}, ${service.color}50)`,
                    }}
                  />

                  <CardContent sx={{ p: 3 }}>
                    {/* Icon */}
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '16px',
                        background: `${service.color}18`,
                        border: `1px solid ${service.color}30`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2.5,
                        color: service.color,
                      }}
                    >
                      {service.icon}
                    </Box>

                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.25, fontSize: '1.1rem' }}>
                      {service.title}
                    </Typography>

                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, mb: 2.5, fontSize: '0.88rem' }}>
                      {service.description}
                    </Typography>

                    {/* Features list */}
                    <Box sx={{ mb: 3 }}>
                      {service.features.map((f) => (
                        <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                          <Box
                            sx={{
                              width: 5,
                              height: 5,
                              borderRadius: '50%',
                              background: service.color,
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.83rem' }}>
                            {f}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Price */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pt: 2.5,
                        borderTop: '1px solid rgba(255,255,255,0.07)',
                      }}
                    >
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 0.25 }}>
                          Starting from
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            fontFamily: 'Poppins',
                            fontWeight: 800,
                            color: service.color,
                            lineHeight: 1,
                          }}
                        >
                          {service.price}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        onClick={() => {
                          const el = document.querySelector('#contact');
                          if (el) el.scrollIntoView({ behavior: 'smooth' });
                        }}
                        sx={{
                          background: `${service.color}20`,
                          color: service.color,
                          border: `1px solid ${service.color}40`,
                          borderRadius: '8px',
                          fontSize: '0.78rem',
                          px: 1.75,
                          py: 0.6,
                          '&:hover': {
                            background: `${service.color}35`,
                          },
                        }}
                      >
                        Get Quote
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
